import { useRef, useEffect } from 'react';
import { Spacer, Flex, Field, Card, Icon, Text, withErrorBoundary } from '@oliasoft-open-source/react-ui-library';
import emptyIcon from 'src/assets/empty.svg';
import { Input } from 'client/common/form-inputs';
import translations from 'src/internationalisation/translation-map.json';
import { useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { getResolver } from 'src/validation/resolver';
import { convertToKebabCase } from 'client/common/utils/string-utils';
import { debounce } from 'lodash';
import { useAutoSave } from 'src/common/auto-save/use-auto-save';
import { autoSaveWait } from 'src/config/config';
import { campaignSchemaValidator } from 'schemas/ajv-validators.ts';
import { addCampaign } from 'store/entities/campaigns/campaigns';
import { ConnectedProps, connect } from 'react-redux';
import { HierarchyInputTable } from 'src/views/campaigns/hierarchy-table/hierarchy-input-table';
import { IActiveCampaign, ICampaignActiveEstimate, IDesignItem } from 'src/common/interfaces/campaigns.interfaces';
import { ButtonWithHierarchyModal } from 'src/views/campaigns/campaign-tabs/button-with-hierarchy.modal.tsx';

type TCampaignInputTab = PropsFromRedux & {
  companyId: string;
  activeCampaign: IActiveCampaign;
  conceptDesigns: IDesignItem[];
  visibleEstimates: ICampaignActiveEstimate[];
  setActiveTabIndex: (index: number) => void;
};

const CampaignInputTab = ({
  addCampaign,
  companyId,
  activeCampaign,
  conceptDesigns,
  visibleEstimates,
  setActiveTabIndex,
}: TCampaignInputTab) => {
  const { estimates, designs, campaignId, name, templates } = activeCampaign;
  const { t } = useTranslation();
  const {
    control,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm({
    mode: 'onChange',
    defaultValues: {
      campaignId: campaignId || null,
      name: name || '',
      designs,
    },
    resolver: getResolver(campaignSchemaValidator),
  });

  useEffect(() => {
    reset({ campaignId, name });
  }, [campaignId, name, reset]);

  const debounceUpdateCampaign = useRef(debounce(addCampaign, autoSaveWait));

  const onSubmit = handleSubmit((data) => {
    debounceUpdateCampaign.current(companyId, {
      ...data,
      designs,
    });
  });

  useAutoSave(onSubmit, watch);

  const showTable = estimates?.length > 0 || conceptDesigns?.length > 0;

  return (
    <>
      <Spacer />
      <Flex>
        <Field label={t(translations.campaigns_campaignName)}>
          <Input
            name="name"
            control={control}
            errors={errors}
            width={300}
            placeholder={t(translations.campaigns_campaignName)}
            testId={`campaigns-input-${convertToKebabCase(t(translations.campaigns_campaignName))}`}
          />
        </Field>
      </Flex>
      <Spacer />
      {!showTable && (
        <Card bordered>
          <Flex alignItems="center" justifyContent="center" direction="column" height="25vh">
            <Icon icon={emptyIcon} />
            <Spacer height="var(--padding-xs)" />
            <Text faint>{t(translations.campaigns_noEstimatesSelected)}</Text>
            <Spacer height="var(--padding-xs)" />
            <ButtonWithHierarchyModal name={name} campaignId={campaignId} companyId={companyId} t={t} />
          </Flex>
        </Card>
      )}
      {showTable && (
        <HierarchyInputTable
          filteredEstimates={estimates}
          conceptDesigns={conceptDesigns}
          campaignId={campaignId}
          name={name}
          activeEstimates={visibleEstimates}
          templates={templates}
          setActiveTabIndex={setActiveTabIndex}
        />
      )}
    </>
  );
};

const mapDispatchToProps = {
  addCampaign,
};

const connector = connect(null, mapDispatchToProps);

type PropsFromRedux = ConnectedProps<typeof connector>;

const Container = withErrorBoundary(connector(CampaignInputTab));

export { Container as CampaignInputTab };
